import { memo } from "react";
import { Heart, ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { priceParts } from "@/lib/pricing";
import BadgeVerified from "@/components/badges/badge_verified";
import { ListerBadge } from "@/components/badges/badge_realtor";
import BadgeBoost from "@/components/badges/badge_boost";

// Memoized: grids re-render often (filters, market, stay type) but each card
// only cares about its own listing.
const CardListing = memo(function CardListing({ listing, onClick, className = "" }) {
  if (!listing) return null;
  const { amount, unit } = priceParts(listing);
  return (
    <button type="button" onClick={onClick} className={cn("group w-full text-left", className)}>
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-night-surface">
        {listing.photos?.[0] ? (
          <img
            src={listing.photos[0]}
            alt={listing.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-night-tint text-night-sub">
            <ImageOff size={22} />
            <span className="text-xs font-medium">Photos coming soon</span>
          </div>
        )}
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {(listing.verified || listing.lister_verified) && <BadgeVerified />}
          <ListerBadge type={listing.lister_type} />
          {listing.boosted && <BadgeBoost />}
        </div>
        <span className="absolute right-3 top-3 text-white">
          <Heart size={22} strokeWidth={2} className="fill-black/40 transition group-hover:fill-black/55" />
        </span>
      </div>
      <div className="mt-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 text-[15px] font-medium text-night-text">{listing.title}</h3>
        </div>
        <p className="mt-0.5 text-sm text-night-sub">
          {[listing.city, listing.province].filter(Boolean).join(", ")}
        </p>
        <p className="mt-0.5 text-sm text-night-sub">
          {listing.beds === 0 ? "Studio" : `${listing.beds} bed`}{listing.dens ? ` + ${listing.dens} den` : ""} · {listing.baths} bath · {listing.sqft} sqft
        </p>
        <p className="mt-1.5 text-[15px]">
          <span className="font-semibold text-night-text">${amount.toLocaleString()}</span>{" "}
          <span className="text-night-sub">{unit}</span>
        </p>
      </div>
    </button>
  );
});

export default CardListing;
